// Dashboard: Status-Tabs + Tabelle pro Tab.
// Default-Tab ist 'Eingegangen', weil dort die Arbeit der Sachbearbeiter:innen
// liegt. Cloud-Shape: schlank (kein OCR/Match/Quelle — das lebt auf dem
// Bot-Host); die Mandant-Zuordnung kommt aus `mandant_source` (ADR-018).

import { useState } from "react";
import { Link } from "react-router-dom";

import { useBelege } from "@/api/hooks";
import type { BelegListItem, BelegStatus } from "@/api/types";
import { MANDANT_SOURCE_LABEL, STATUS_LABEL, STATUS_ORDER } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";

import "./Dashboard.css";

const TABS: readonly BelegStatus[] = STATUS_ORDER;

export function Dashboard() {
  const [active, setActive] = useState<BelegStatus>("eingegangen");
  const belege = useBelege(active);

  return (
    <>
      <PageHeader
        title="Belege"
        subtitle="Alles, was in der Cloud eingegangen ist."
      />

      <StatusTabs active={active} onChange={setActive} />

      {belege.isLoading ? (
        <div className="bmd-card"><Loader /></div>
      ) : belege.isError ? (
        <ErrorBox message={belege.error.message} />
      ) : (belege.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="Keine Belege in dieser Liste"
          description={
            active === "eingegangen"
              ? "Sobald ein Beleg hochgeladen wird, taucht er hier auf."
              : `Es gibt aktuell keine Belege im Status '${STATUS_LABEL[active]}'.`
          }
        />
      ) : (
        <BelegTable belege={belege.data ?? []} />
      )}
    </>
  );
}

// --- Status-Tabs ----------------------------------------------------

function StatusTabs({
  active,
  onChange,
}: {
  active: BelegStatus;
  onChange: (s: BelegStatus) => void;
}) {
  return (
    <div className="bmd-tabs" role="tablist">
      {TABS.map((status) => {
        const isActive = active === status;
        return (
          <button
            key={status}
            role="tab"
            type="button"
            aria-selected={isActive}
            className={`bmd-tab${isActive ? " bmd-tab-active" : ""}`}
            onClick={() => onChange(status)}
          >
            <span className="bmd-tab-label">{STATUS_LABEL[status]}</span>
          </button>
        );
      })}
    </div>
  );
}

// --- Tabelle --------------------------------------------------------

function BelegTable({ belege }: { belege: BelegListItem[] }) {
  return (
    <table className="bmd-table bmd-dashboard-table">
      <thead>
        <tr>
          <th>Datei</th>
          <th>Mandant</th>
          <th>Zuordnung</th>
          <th>Belegdatum</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {belege.map((b) => (
          <BelegRow key={b.id} beleg={b} />
        ))}
      </tbody>
    </table>
  );
}

function BelegRow({ beleg }: { beleg: BelegListItem }) {
  const navigateUrl = `/belege/${beleg.id}`;
  const open = () => window.location.assign(navigateUrl);

  return (
    <tr onClick={open}>
      <td>
        <Link
          to={navigateUrl}
          className="bmd-dashboard-filename"
          onClick={(e) => e.stopPropagation()}
        >
          {beleg.original_filename}
        </Link>
      </td>
      <td>
        {beleg.mandant ? (
          <div className="bmd-stack-2">
            <div className="bmd-dashboard-mandant">{beleg.mandant.name}</div>
            <div className="bmd-mono bmd-subtle">
              {beleg.mandant.bmd_kundennr}
            </div>
          </div>
        ) : (
          <span className="bmd-subtle">— nicht zugeordnet</span>
        )}
      </td>
      <td className="bmd-subtle">
        {MANDANT_SOURCE_LABEL[beleg.mandant_source]}
      </td>
      <td className="bmd-mono">{beleg.belegdatum ?? "—"}</td>
      <td>
        <StatusBadge status={beleg.status} />
      </td>
    </tr>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="bmd-card bmd-error">
      <strong>Konnte die Belege nicht laden.</strong>
      <div className="bmd-mono bmd-subtle">{message}</div>
    </div>
  );
}
